import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Select, Spin, Badge, Tag, Tooltip } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { ChevronDown, Check, Search, Info } from 'lucide-react';
import { HtmlContent } from '@client/utils/html_content';
import DownIcon from '../../../../img/ant-down-icon.svg';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { useDebouncedValue } from '@client/hooks/useDebouncedValue';
import { useElasticsearchOptions } from '@client/hooks/useElasticsearchOptions';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';
import CollapsibleTags from './CollapsibleTags';
import TutorialButton from '@client/components/ui/TutorialButton';

/**
 * UniversalElasticsearchSelect — UI-only component.
 *
 * Owns: input text, language selection, mobile-sheet state, label cache.
 * Reads (never writes) options/loading/pagination from useElasticsearchOptions.
 *
 * What this component intentionally does NOT do:
 *   - manage `options`, `currentPage`, `hasMore`, `loading` state
 *   - debounce inside callbacks (we debounce the value, not the function)
 *   - track in-flight requests or sequence numbers
 *   - reset state in effects when filters/facets/language change
 *
 * All of that is RQ's job. See useElasticsearchOptions.js for the rationale.
 */

/**
 * @typedef {Object} ElasticsearchOption
 * @property {string} label - Display label
 * @property {string} value - Option value
 * @property {number} [count] - Result count
 * @property {boolean} [highlighted] - Highlight status
 */

/**
 * @typedef {Object} ElasticsearchSelectProps
 * @property {string} document - Document type for Elasticsearch
 * @property {string} field - Field to search in
 * @property {string} [label] - Label translation key
 * @property {number} [debounceTimeout] - Search debounce in ms
 * @property {number} [pageSize] - Results per page
 * @property {string} [sortBy] - Sort field
 * @property {string} [sortOrder] - Sort direction
 * @property {boolean} [showCount] - Display result counts
 * @property {boolean} [infinite] - Enable infinite scroll
 * @property {any} value - Selected value(s)
 * @property {Function} onChange - Change handler
 * @property {boolean} [showSelectedTags] - Show selected items as tags
 * @property {string} [value_field] - Value field mapping
 * @property {string} [label_field] - Label field mapping
 * @property {Object} [languageFields] - Language field mappings
 * @property {Object} [languageLabels] - Language display labels
 * @property {string} [defaultLanguage] - Default language code
 * @property {boolean} [showLanguageSelector] - Show language switcher
 * @property {Function} [labelTransform] - Function to transform option labels (label, value) => transformedLabel
 */

const CONFIG = {
  DEBOUNCE_TIMEOUT: 150,
  PAGE_SIZE: 20,
  SORT_BY: 'count',
  SORT_ORDER: 'desc',
  STORAGE_PREFIX: 'lang_',
};

const TRANSLATIONS = {
  LOADING: 'Chargement...',
  NO_RESULTS: 'Aucun résultat trouvé',
  SEARCH: 'Rechercher...',
};

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const OptionRenderer = ({ option, showCount }) => {
  // AntD's optionRender wraps our option: `option.data` is the original option
  // object, so our inner data lives at `option.data.data`. Handle both paths.
  const optData = option.data?.data || option.data || {};
  return (
    <div className="elasticsearch-option">
      <div className="flex-1 min-w-0">
        <span
          className={`option-label ${optData.highlighted ? 'highlighted' : ''}`}
        >
          <HtmlContent html={option.label} />
        </span>
        {optData.matchInfo && (
          <div className="text-xs text-gray-400">
            {optData.matchInfo.label} : {optData.matchInfo.value}
          </div>
        )}
      </div>
      {showCount && optData.count && (
        <Badge count={optData.count} className="option-count" />
      )}
    </div>
  );
};

// Globe icon + the word « Langue » sits to the LEFT of the selector,
// matching the pattern used by CultureTreeSelect (SP-408 rule 14/17).
const LanguageSelector = ({
  selectedLanguage,
  languageLabels,
  onChange,
  langLabel,
}) => (
  <Select
    size="small"
    value={selectedLanguage}
    onChange={onChange}
    options={Object.entries(languageLabels).map(([code, label]) => ({
      value: code,
      label: label,
    }))}
    className="language-selector"
    prefix={
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <GlobalOutlined />
        <span>{langLabel}</span>
      </span>
    }
  />
);

/**
 * Detect a secondary-field match for an option so the dropdown can explain
 * why the option appeared (e.g. "Synonyme : foo" when query matched a synonym
 * but not the primary name). Pure function over (option, query, searchFields).
 */
function detectMatchInfo(option, query, searchFields) {
  if (
    !query
    || !Array.isArray(searchFields)
    || !option.source
    || !searchFields.some((sf) => typeof sf === 'object')
  ) {
    return null;
  }
  const q = query.toLowerCase();
  const primaryHit = String(option.label).toLowerCase().includes(q);
  if (primaryHit) return null;

  for (const sf of searchFields) {
    if (typeof sf !== 'object' || sf.primary || !sf.sourceField) continue;
    const vals = option.source[sf.sourceField];
    const arr = Array.isArray(vals)
      ? vals
      : vals != null
        ? [vals]
        : [];
    const toStr = (v) =>
      typeof v === 'object' && v !== null
        ? v.name || v.label || v.id || JSON.stringify(v)
        : String(v);
    const hit = arr.find((v) => toStr(v).toLowerCase().includes(q));
    if (hit) {
      return { label: sf.label || sf.sourceField, value: toStr(hit) };
    }
  }
  return null;
}

/**
 * Elasticsearch-powered select component with infinite scroll.
 * @component
 */
const ElasticsearchSelect = React.forwardRef(({
  document,
  label = 'common|Select',
  field,
  apiEndpoint = null,
  apiFilters = null,
  debounceTimeout = CONFIG.DEBOUNCE_TIMEOUT,
  pageSize = CONFIG.PAGE_SIZE,
  sortBy = CONFIG.SORT_BY,
  sortOrder = CONFIG.SORT_ORDER,
  showCount = true,
  infinite = true,
  value,
  onChange,
  showSelectedTags = true,
  value_field = null,
  label_field = null,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = 'fr',
  showLanguageSelector = false,
  labelTransform = null,
  filters = null,
  mode = 'multiple',
  placeholder,
  initialLabels = null, // Labels for cold start restoration [{value, label}, ...]
  maxVisibleTags = 1,
  // Cross-field faceting: { [es_field_path]: string[] } of currently selected
  // values from sibling filters. The backend skips the self-field automatically
  // so this dropdown still surfaces every possible value for its own field.
  activeFacets = null,
  // Optional list of ES field paths to union-search against the user's `q`
  // (e.g. a substance dropdown searching name OR cas_numbers OR synonyms).
  searchFields = null,
  // Optional list of extra _source paths to include alongside each bucket.
  includeSourceFields = null,
  // Optional (option, sourceDict) => string — composes the displayed label
  // from backend extras.
  formatOptionLabel = null,
  // Optional info tooltip shown next to the label. { title, content }.
  infoBulle = null,
  // Tutorial slot key — renders a TutorialButton if assigned.
  tutorialSlotKey = null,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // -------------------------------------------------------------------------
  // UI state — the only state this component owns
  // -------------------------------------------------------------------------
  const [inputValue, setInputValue] = useState(''); // controlled desktop search input
  const [mobileSearchValue, setMobileSearchValue] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [localValue, setLocalValue] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return null;
    const saved = localStorage.getItem(`${CONFIG.STORAGE_PREFIX}${field}`);
    return saved || defaultLanguage;
  });
  // Lazy fetch: don't fire requests until the dropdown opens at least once,
  // unless the component already has selected values to resolve labels for.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  const currentLabelField = useMemo(
    () => languageFields?.[selectedLanguage] || label_field,
    [languageFields, selectedLanguage, label_field],
  );

  // -------------------------------------------------------------------------
  // Debounced query — single source of truth fed to the data hook
  // -------------------------------------------------------------------------
  const activeRawInput = isMobile ? mobileSearchValue : inputValue;
  const debouncedQuery = useDebouncedValue(activeRawInput, debounceTimeout);

  const hasPreSelectedValues = useMemo(() => {
    if (Array.isArray(value)) return value.length > 0;
    return value != null && value !== '';
  }, [value]);

  // -------------------------------------------------------------------------
  // Data fetching — fully delegated to React Query
  // -------------------------------------------------------------------------
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useElasticsearchOptions({
    document,
    field,
    query: debouncedQuery,
    pageSize,
    sortBy,
    sortOrder,
    valueField: value_field,
    labelField: currentLabelField,
    filters,
    facets: activeFacets,
    searchFields,
    includeSourceFields,
    apiEndpoint,
    apiFilters,
    enabled: hasOpenedOnce || hasPreSelectedValues,
  });

  // Flatten infinite-query pages → raw options.
  const rawOptions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((p) => p.options || []);
  }, [data]);

  // Persistent label cache so selected-tag labels don't disappear when the
  // options list refreshes (e.g. after a cross-facet refetch strips the
  // currently-selected value out of the visible bucket set).
  const labelCacheRef = useRef(new Map());

  // Apply consumer formatting (formatOptionLabel, labelTransform) and
  // secondary-field match annotations. Pure derivation from rawOptions +
  // current query — no side effects, nothing to invalidate.
  const formattedOptions = useMemo(() => {
    return rawOptions.map((option) => {
      let resolvedLabel;
      if (typeof formatOptionLabel === 'function') {
        resolvedLabel = formatOptionLabel(option, option.source || {});
      }
      if (resolvedLabel == null) {
        resolvedLabel = labelTransform
          ? labelTransform(option.label, option.value)
          : option.label;
      }
      return {
        label: resolvedLabel,
        value: option.value,
        data: {
          count: option.count,
          highlighted: option.highlighted,
          source: option.source,
          matchInfo: detectMatchInfo(option, debouncedQuery, searchFields),
        },
      };
    });
  }, [rawOptions, formatOptionLabel, labelTransform, debouncedQuery, searchFields]);

  // Build the resolver used by tags + mobile display.
  const labelMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(initialLabels)) {
      initialLabels.forEach((item) => {
        if (item?.value != null) {
          map.set(item.value, item.label || item.name || item.value);
        }
      });
    }
    labelCacheRef.current.forEach((label, val) => {
      map.set(val, label);
    });
    formattedOptions.forEach((opt) => {
      if (opt?.value != null) {
        map.set(opt.value, opt.label);
        labelCacheRef.current.set(opt.value, opt.label);
      }
    });
    return map;
  }, [initialLabels, formattedOptions]);

  const getLabel = useCallback(
    (val) => labelMap.get(val) || val,
    [labelMap],
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleSearch = useCallback((raw) => {
    setInputValue(raw);
  }, []);

  const handleLanguageChange = useCallback(
    (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem(`${CONFIG.STORAGE_PREFIX}${field}`, lang);
      // No manual options reset here — changing currentLabelField changes the
      // RQ query key and triggers a fresh fetch automatically.
    },
    [field],
  );

  const handleDropdownVisibleChange = useCallback((open) => {
    if (open) setHasOpenedOnce(true);
  }, []);

  const handleScroll = useCallback(
    (event) => {
      if (!infinite || !hasNextPage || isFetchingNextPage) return;
      const { target } = event;
      const atBottom =
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 5;
      if (atBottom) fetchNextPage();
    },
    [infinite, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const handleRemoveTag = useCallback(
    (removedValue) => {
      const newValue = Array.isArray(value)
        ? value.filter((v) => v !== removedValue)
        : undefined;
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const handleChange = useCallback(
    (newValue) => {
      const valueArray = Array.isArray(newValue)
        ? newValue
        : (newValue ? [newValue] : []);
      const selectedItems = valueArray.map((val) => ({
        value: val,
        label: getLabel(val),
      }));
      // Mirror the previous `autoClearSearchValue` behaviour.
      setInputValue('');
      const isMultiple = mode === 'multiple';
      const returnValue = isMultiple ? valueArray : valueArray[0] || null;
      onChange(returnValue, selectedItems);
    },
    [getLabel, onChange, mode],
  );

  // External language sync (e.g. another instance of the same field changed
  // language and broadcast it via window event).
  useEffect(() => {
    if (!languageFields) return;
    const handler = (event) => handleLanguageChange(event.detail);
    const eventName = `languageChanged_${field}`;
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [field, languageFields, handleLanguageChange]);

  // Mobile: sync localValue + reset search when sheet opens.
  useEffect(() => {
    if (sheetOpen) {
      const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
      setLocalValue(valueArray.filter(Boolean));
      setMobileSearchValue('');
      setHasOpenedOnce(true);
    }
  }, [sheetOpen, value]);

  // Append loading sentinel for desktop infinite scroll.
  const selectOptions = useMemo(() => {
    const out = formattedOptions.map((option) => ({ ...option }));
    if (isFetchingNextPage && infinite) {
      out.push({
        value: '__loading__',
        label: TRANSLATIONS.LOADING,
        disabled: true,
      });
    }
    return out;
  }, [formattedOptions, isFetchingNextPage, infinite]);

  // Mobile handlers
  const handleMobileSearchChange = useCallback((e) => {
    setMobileSearchValue(e.target.value);
  }, []);

  const handleMobileSelect = useCallback(
    (optionValue) => {
      const isMultiple = mode === 'multiple';
      if (isMultiple) {
        setLocalValue((prev) =>
          prev.includes(optionValue)
            ? prev.filter((v) => v !== optionValue)
            : [...prev, optionValue],
        );
      } else {
        const selectedItem = { value: optionValue, label: getLabel(optionValue) };
        onChange([optionValue], [selectedItem]);
        setSheetOpen(false);
      }
    },
    [mode, getLabel, onChange],
  );

  const handleMobileDone = useCallback(() => {
    const selectedItems = localValue.map((val) => ({
      value: val,
      label: getLabel(val),
    }));
    onChange(localValue, selectedItems);
    setSheetOpen(false);
  }, [localValue, getLabel, onChange]);

  const handleMobileScroll = useCallback(
    (e) => {
      if (!infinite || !hasNextPage || isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollTop + clientHeight >= scrollHeight - 50) fetchNextPage();
    },
    [infinite, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const mobileDisplayValue = useMemo(() => {
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    if (valueArray.length === 0) return '';
    if (valueArray.length === 1) {
      return stripHtml(String(getLabel(valueArray[0])));
    }
    return t('common|{{count}} sélectionné(s)', { count: valueArray.length });
  }, [value, getLabel, t]);

  const isMultiple = mode === 'multiple';
  const selectedCount = localValue.length;

  // -------------------------------------------------------------------------
  // Mobile render
  // -------------------------------------------------------------------------
  if (isMobile) {
    return (
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-gray-700">
            {t(label)}
          </label>
          {showLanguageSelector && languageFields && languageLabels && (
            <LanguageSelector
              selectedLanguage={selectedLanguage}
              languageLabels={languageLabels}
              onChange={handleLanguageChange}
              langLabel={t('common|Langue')}
            />
          )}
        </div>

        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span
            className={`text-xs flex-1 truncate ${
              mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            {mobileDisplayValue || placeholder || t('common|Search...')}
          </span>
          {isFetching ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={
            isMultiple && selectedCount > 0
              ? `${t(label)} (${selectedCount})`
              : t(label)
          }
          showDone={isMultiple}
          onDone={handleMobileDone}
          doneLabel={t('common|Terminé')}
          doneDisabled={isMultiple && selectedCount === 0}
        >
          {showLanguageSelector && languageFields && languageLabels && (
            <div className="px-3 py-2 border-b border-gray-100">
              <LanguageSelector
                selectedLanguage={selectedLanguage}
                languageLabels={languageLabels}
                onChange={handleLanguageChange}
                langLabel={t('common|Langue')}
              />
            </div>
          )}

          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={handleMobileSearchChange}
              />
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            onScroll={handleMobileScroll}
          >
            {isFetching && formattedOptions.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isFetching && formattedOptions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">
                  {t('common|Aucun résultat trouvé')}
                </div>
              </div>
            )}

            {formattedOptions.length > 0 && (
              <div className="py-1">
                {formattedOptions.map((option) => {
                  const isSelected = localValue.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                      onClick={() => handleMobileSelect(option.value)}
                    >
                      {isMultiple ? (
                        <div
                          className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected
                              ? 'bg-[#207d54] border-[#207d54]'
                              : 'border-gray-300'
                          }`}
                        >
                          {isSelected && <Check size={10} color="#fff" />}
                        </div>
                      ) : (
                        <div
                          className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected ? 'border-[#207d54]' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 bg-[#207d54] rounded-full" />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">
                          <HtmlContent html={option.label} />
                        </div>
                        {showCount && option.data?.count && (
                          <div className="text-xs text-gray-500">
                            {option.data.count} résultats
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isFetchingNextPage && formattedOptions.length > 0 && (
              <div className="flex items-center justify-center py-4">
                <Spin size="small" />
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Desktop render
  // -------------------------------------------------------------------------
  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        <label className="select-label">
          {t(label)}
          {infoBulle && (
            <Tooltip
              title={
                <div>
                  {infoBulle.title && (
                    <div className="font-semibold mb-1">{t(infoBulle.title)}</div>
                  )}
                  <div>{t(infoBulle.content)}</div>
                </div>
              }
              placement="top"
            >
              <Info
                size={14}
                className="inline-block ml-1 text-gray-400 cursor-help"
                style={{ verticalAlign: 'middle' }}
              />
            </Tooltip>
          )}
        </label>
        {tutorialSlotKey && <TutorialButton slotKey={tutorialSlotKey} />}
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            languageLabels={languageLabels}
            onChange={handleLanguageChange}
            langLabel={t('common|Langue')}
          />
        )}
      </div>

      <Select
        ref={ref}
        key={`${field}-${selectedLanguage}`}
        className="elasticsearch-select"
        mode={mode}
        labelInValue={false}
        filterOption={false}
        onSearch={handleSearch}
        searchValue={inputValue}
        autoClearSearchValue={false}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onPopupScroll={infinite ? handleScroll : undefined}
        placeholder={t('common|Search...') || TRANSLATIONS.SEARCH}
        notFoundContent={
          isFetching ? (
            <Spin size="small" />
          ) : (
            t('common|No results found') || TRANSLATIONS.NO_RESULTS
          )
        }
        loading={isFetching}
        value={value}
        onChange={handleChange}
        showSearch
        options={selectOptions}
        optionRender={(option) => (
          <OptionRenderer option={option} showCount={showCount} />
        )}
        tagRender={(tagProps) => {
          const { value: tagValue, closable, onClose, isMaxTag } = tagProps;

          // Overflow "+N sélection(s)" indicator when maxTagCount is exceeded
          if (isMaxTag) {
            const count = Array.isArray(value)
              ? Math.max(0, value.length - 1)
              : 0;
            return (
              <Tag style={{ marginRight: 3 }}>
                +{count} {count > 1 ? 'sélections' : 'sélection'}
              </Tag>
            );
          }

          const displayLabel = getLabel(tagValue);
          const cleanLabel = stripHtml(String(displayLabel || tagValue || ''));
          const truncatedLabel =
            cleanLabel.length > 15
              ? `${cleanLabel.substring(0, 15)}...`
              : cleanLabel;
          return (
            <Tooltip title={cleanLabel}>
              <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                {truncatedLabel}
              </Tag>
            </Tooltip>
          );
        }}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        {...props}
      />

      {showSelectedTags && (
        <CollapsibleTags
          value={value}
          getLabel={getLabel}
          onRemove={handleRemoveTag}
          maxVisible={maxVisibleTags}
        />
      )}
    </div>
  );
});

ElasticsearchSelect.displayName = 'ElasticsearchSelect';

export default React.memo(ElasticsearchSelect);